import type { OnDestroy, OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { DialogRef } from '@angular/cdk/dialog';
import { LtTextInputComponent } from '@app/components/core/lt-text-input/lt-text-input.component';
import type { SelectOption } from '@app/components/core/lt-select/lt-select.component';
import { LtSelectComponent } from '@app/components/core/lt-select/lt-select.component';
import { LtTranslatePipe } from '@app/pipes/lt-translate.pipe';
import type { ShoppingCartSubject } from '@app/interface/subject/shopping-cart-subject.interface';
import type { ModalResultProvider } from '@app/components/modals/config/composable-modal.config';
import { angularjs } from '@app/service/migration-helpers/angularjs.injection';
import { LtButtonComponent } from '@app/components/core/lt-button/lt-button.component';
import App from '@app/app';
import { downgradeComponent } from '@angular/upgrade/static';
import { LtIconComponent } from '@app/components/core/lt-icon/lt-icon.component';
import { SESSION_STORAGE_KEYS } from '@app/constant/session-storage-keys';
import { UibPopoverComponentWrapper } from '@app/components/core/uib-popover/uib-popover.component';
import { StudentPersonalizationService } from '@app/service/student-detail/student-personalization.service';
import { StudentBannerService } from '@app/service/student-banner.service';
import { sanitizeInput } from '@app/utils/sanitize';
import { SUBJECT_NAME_MAX_LENGTH, TEACHER_NAME_MAX_LENGTH } from '@app/constant/form-validation.constants';
import { getSubjectNameError } from '@app/service/student-detail/student-name.validation';
import { hasRequiredSubjectDetails } from '@app/service/student-detail/subject-completeness';

type StudentDetailsFormData = {
  subjectFirstName: string;
  preferredFirstName: string;
  subjectLastName: string;
  subjectTeacherName: string | null;
  subjectGrade: string;
  subjectRelationship: string;
  disclaimerAccepted: boolean;
};

/**
 * An entry may have been written mid-entry, or by a build that predates `disclaimerAccepted`, so
 * every field is optional until `isSubjectComplete` says otherwise.
 */
type StoredStudentDetails = Partial<StudentDetailsFormData>;

/**
 * Shape stored in session storage: a map of subjectId → saved form data. Only subjects the user has
 * already touched have an entry, so a lookup may miss.
 */
type StudentDetailsDraft = Record<string, StoredStudentDetails | undefined>;
type SubjectContext = Pick<ShoppingCartSubject['jobContext'], 'jobName' | 'eventDate' | 'accountName'>;
type StudentDetailsFormState = {
  subjectFirstName?: string | null;
  preferredFirstName?: string | null;
  subjectLastName?: string | null;
  subjectTeacherName?: string | null;
  subjectGrade?: string | null;
  subjectRelationship?: string | null;
  disclaimerAccepted?: boolean;
};

export const RELATIONSHIP_OPTIONS: SelectOption[] = [
  { value: 'ParentGuardian', label: 'PICTURE_DAY_PARENT_GUARDIAN_$COUNTRY' },
  { value: 'GrandParents', label: 'PICTURE_DAY_GRANDPARENT_$COUNTRY' },
  { value: 'Others', label: 'PICTURE_DAY_OTHER_$COUNTRY' }
] as const;

export type RelationshipOption = (typeof RELATIONSHIP_OPTIONS)[number]['value'];

@Component({
  selector: 'lt-student-details',
  imports: [
    LtTextInputComponent,
    LtSelectComponent,
    LtTranslatePipe,
    LtButtonComponent,
    LtIconComponent,
    UibPopoverComponentWrapper
  ],
  templateUrl: './student-details.component.html',
  styleUrl: './student-details.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class StudentDetailsComponent implements OnInit, OnDestroy, ModalResultProvider<Partial<ShoppingCartSubject>[]> {
  private readonly gradeService = inject(angularjs.GradeService);
  private readonly jobStore = inject(angularjs.JobStore);
  private readonly storageService = inject(angularjs.StorageService);
  private readonly subjectStore = inject(angularjs.SubjectStore);
  private readonly dialogRef = inject(DialogRef, { optional: true });
  private readonly personalizationService = inject(StudentPersonalizationService);
  private readonly studentBannerService = inject(StudentBannerService);

  subjects = input.required<ReadonlyArray<ShoppingCartSubject>>();
  completed = output<Partial<ShoppingCartSubject>[]>();

  firstName = signal('');
  preferredFirstName = signal('');
  lastName = signal('');
  teacherLastName = signal('');
  grade = signal('');
  relationship = signal('');
  disclaimerAccepted = signal(false);
  currentSubjectIndex = signal(0);

  private readonly savedResults = signal<Map<number, StoredStudentDetails>>(new Map());
  private completedSuccessfully = false;

  public relationshipOptions: SelectOption[] = RELATIONSHIP_OPTIONS;
  public gradeOptions: SelectOption[] = [];

  currentSubject = computed<ShoppingCartSubject | undefined>(() => this.subjects()[this.currentSubjectIndex()]);
  currentSubjectContext = computed<SubjectContext | undefined>(() => {
    const subject = this.currentSubject();
    return subject?.jobContext ?? (subject?.portraitDashboard as unknown as SubjectContext | undefined);
  });
  currentJobName = computed(() => {
    const subject = this.currentSubject();
    return (
      this.currentSubjectContext()?.jobName ?? this.jobStore.getJob(subject?.portraitDashboard.jobKey)?.jobName ?? ''
    );
  });
  currentEventDate = computed(() => this.currentSubjectContext()?.eventDate ?? '');
  currentAccountName = computed(() => this.currentSubjectContext()?.accountName ?? '');
  currentGradYear = computed(
    () => this.jobStore.getJobByJobKey(this.currentSubject()?.portraitDashboard?.jobKey)?.gradYear
  );
  currentEventNameAndYear = computed(() =>
    this.studentBannerService.getEventNameAndYear(
      this.currentJobName(),
      this.currentEventDate(),
      this.currentGradYear()
    )
  );
  isClassRoomGroup = computed(() => {
    const subject = this.currentSubject();
    return Boolean(subject && this.jobStore.isClassRoomGroup(subject.portraitDashboard.jobKey));
  });
  isMultiple = computed(() => this.subjects().length > 1);
  isLastSubject = computed(() => this.currentSubjectIndex() === this.subjects().length - 1);
  showPreferredFirstName = computed(() => {
    const subject = this.currentSubject();
    return Boolean(
      subject && (this.personalizationService.hasSelectedPersonalization(subject) || subject.hasPackagePersonalization)
    );
  });
  preferredFirstNameMaxLength = computed(() => this.personalizationService.getMaxLength(this.currentSubject()));
  // Gated to match isFormStateComplete(): the hidden field's error must never surface anywhere.
  // Harmless today (the message only renders inside the showPreferredFirstName() block), but it
  // keeps this computed safe for any future consumer that gates on it.
  preferredFirstNameError = computed(() =>
    this.showPreferredFirstName()
      ? this.personalizationService.getError(this.preferredFirstName(), this.preferredFirstNameMaxLength())
      : null
  );
  preferredFirstNameErrorMessage = computed(() => this.preferredFirstNameError() ?? '');

  firstNameError = computed(() => getSubjectNameError(this.firstName(), this.subjectNameMaxLength));
  firstNameErrorMessage = computed(() => this.firstNameError() ?? '');

  lastNameError = computed(() => getSubjectNameError(this.lastName(), this.subjectNameMaxLength));
  lastNameErrorMessage = computed(() => this.lastNameError() ?? '');

  teacherLastNameError = computed(() =>
    this.isClassRoomGroup() ? getSubjectNameError(this.teacherLastName(), this.teacherNameMaxLength) : null
  );
  teacherLastNameErrorMessage = computed(() => this.teacherLastNameError() ?? '');

  readonly subjectNameMaxLength = SUBJECT_NAME_MAX_LENGTH;
  readonly teacherNameMaxLength = TEACHER_NAME_MAX_LENGTH;

  headingKey = computed(() => (this.isMultiple() ? 'STU_BANNER_STUDENT_COUNT' : 'STUDENT_DETAILS'));

  headingParams = computed(() =>
    this.isMultiple()
      ? { currentIndex: String(this.currentSubjectIndex() + 1), totalCount: String(this.subjects().length) }
      : undefined
  );

  buttonLabelKey = computed(() => {
    if (!this.isMultiple()) return 'SAVE_AND_CONTINUE';
    return this.isLastSubject() ? 'SAVE_AND_CHECKOUT' : 'SAVE_AND_NEXT';
  });

  backStudentLabelParams = computed(() => ({ index: String(this.currentSubjectIndex()) }));

  relationshipDisclaimerKey = computed(() =>
    this.relationship() === 'ParentGuardian'
      ? 'PICTURE_DAY_PARENT_GUARDIAN_DISCLAIMER_$COUNTRY'
      : 'PICTURE_DAY_GRANDPARENT_OTHER_DISCLAIMER_$COUNTRY'
  );

  /** The live form as a plain state object, so stored and live details share one completeness check. */
  private readonly currentFormState = computed<StudentDetailsFormState>(() => ({
    subjectFirstName: this.firstName(),
    preferredFirstName: this.preferredFirstName(),
    subjectLastName: this.lastName(),
    subjectTeacherName: this.teacherLastName(),
    subjectGrade: this.grade(),
    subjectRelationship: this.relationship(),
    disclaimerAccepted: this.disclaimerAccepted()
  }));

  isSaveDisabled = computed(() => !this.isFormStateComplete(this.currentSubject(), this.currentFormState()));

  onFirstNameChange(value: string): void {
    this.firstName.set(sanitizeInput(value, '') ?? '');
  }

  onLastNameChange(value: string): void {
    this.lastName.set(sanitizeInput(value, '') ?? '');
  }

  onTeacherLastNameChange(value: string): void {
    this.teacherLastName.set(sanitizeInput(value, '') ?? '');
  }

  ngOnInit(): void {
    this.gradeOptions = this.gradeService.getGrades().map((g: string) => ({
      value: g,
      label: `GRADE_${g}`
    }));

    this.restoreFromStorage();
  }

  ngOnDestroy(): void {
    if (!this.completedSuccessfully) {
      this.persistDraft();
    }
  }

  private restoreFromStorage(): void {
    const subjects = this.subjects();
    if (!subjects.length) {
      this.restoreResult(0);
      return;
    }

    const draft: StudentDetailsDraft | null = this.storageService.get(SESSION_STORAGE_KEYS.STUDENT_DETAILS_INFO);
    if (!draft) {
      this.restoreResult(0);
      return;
    }

    subjects.forEach((subject, index) => {
      const saved = draft[subject.id];
      if (!saved) return;
      this.savedResults.update((map) => new Map(map).set(index, saved));
    });

    // Resume on the first subject still missing required details. A stored entry may be a
    // mid-entry draft, so having one is not proof the subject is done — checking only for its
    // presence let a dismissed popup skip the student it was opened for. Falls back to the last
    // subject when every entry is complete.
    const firstIncompleteIndex = subjects.findIndex((subject, index) => !this.isSubjectComplete(subject, index));
    const resumeIndex = firstIncompleteIndex === -1 ? subjects.length - 1 : firstIncompleteIndex;

    this.currentSubjectIndex.set(resumeIndex);
    this.restoreResult(resumeIndex);
  }

  /**
   * Whether the stored entry for a subject holds every required detail. Guards both the resume
   * position and the checkout result so a partially filled subject can never pass as done.
   */
  private isSubjectComplete(subject: ShoppingCartSubject, index: number): boolean {
    const saved = this.savedResults().get(index);
    if (!saved) {
      return false;
    }

    const state = this.toFormState(saved);
    // Entries written before `disclaimerAccepted` existed infer it from the required fields.
    const disclaimerAccepted = saved.disclaimerAccepted ?? this.inferDisclaimerAccepted(state);

    return this.isFormStateComplete(subject, { ...state, disclaimerAccepted });
  }

  /**
   * The single definition of "these details are done": every required field present, the disclaimer
   * accepted, and no validation error. Shared by the save button and by the stored-entry checks so
   * the two can never drift into disagreeing about what counts as complete.
   */
  private isFormStateComplete(subject: ShoppingCartSubject | undefined, state: StudentDetailsFormState): boolean {
    if (!subject) {
      return false;
    }

    // hasPersonalization covers both an explicitly selected add-on and a package that includes
    // Name & Grade on All Portraits. The preferred-name field is shown (showPreferredFirstName) and
    // validated by the error check below in both cases.
    const hasPersonalization =
      this.personalizationService.hasSelectedPersonalization(subject) || Boolean(subject.hasPackagePersonalization);

    // Required fields (first/last name, grade, relationship) — the shared gate the cart also uses.
    // nameOnText is only gated by hasPackagePersonalization (not hasSelectedPersonalization): the
    // explicit add-on case is enforced by the error check below, not by hasRequiredSubjectDetails.
    if (
      !hasRequiredSubjectDetails({
        subjectFirstName: state.subjectFirstName,
        subjectLastName: state.subjectLastName,
        subjectGrade: state.subjectGrade,
        subjectRelationship: state.subjectRelationship,
        hasPackagePersonalization: subject.hasPackagePersonalization,
        nameOnText: subject.hasPackagePersonalization
          ? state.preferredFirstName?.trim() || state.subjectFirstName?.trim() || null
          : null
      })
    ) {
      return false;
    }

    if (!state.disclaimerAccepted) {
      return false;
    }

    const isClassRoomGroup = Boolean(this.jobStore.isClassRoomGroup(subject.portraitDashboard.jobKey));
    // Only validate the preferred name when its input is actually rendered, for the same reason the
    // teacher name is gated on isClassRoomGroup: neither the save button nor the stored-entry check
    // may be blocked by a field the user cannot see or correct.
    //
    // A personalization name the user typed and then abandoned ("No thanks") stays in the store —
    // hasSelectedPersonalization() reads skuCode while getPersonalizationName() reads value — so it
    // still prefills preferredFirstName while the input is hidden. Ungated, an invalid character in
    // that abandoned name both kills Save with no visible error AND makes isSubjectComplete() false
    // forever, so getModalResult() drops the subject's details on the way to checkout.
    //
    // The value is never submitted in this state: getModalResult() sends nameOnText = null whenever
    // personalization is neither selected nor package-included.
    const errors = [
      getSubjectNameError(state.subjectFirstName, this.subjectNameMaxLength),
      getSubjectNameError(state.subjectLastName, this.subjectNameMaxLength),
      isClassRoomGroup ? getSubjectNameError(state.subjectTeacherName, this.teacherNameMaxLength) : null,
      hasPersonalization
        ? this.personalizationService.getError(
            state.preferredFirstName ?? '',
            this.personalizationService.getMaxLength(subject)
          )
        : null
    ];

    return errors.every((error) => !error);
  }

  private persistDraft(): void {
    const draft: StudentDetailsDraft = {};

    // Persist all confirmed subjects
    this.savedResults().forEach((data, index) => {
      const subjectId = this.subjects()[index]?.id;
      if (subjectId) draft[subjectId] = data;
    });

    // Persist the in-progress current subject if any field is filled
    const currentSubject = this.currentSubject();
    const hasPartialData =
      this.preferredFirstName() ||
      this.firstName() ||
      this.lastName() ||
      this.teacherLastName() ||
      this.grade() ||
      this.relationship();

    // The live form is newer than whatever is stored for this subject, so it always wins — gating
    // on the absence of a stored entry would discard edits made after reopening a draft.
    if (hasPartialData && currentSubject) {
      draft[currentSubject.id] = this.buildCurrentResult();
    }

    if (Object.keys(draft).length > 0) {
      this.storageService.set(SESSION_STORAGE_KEYS.STUDENT_DETAILS_INFO, draft);
    }
  }

  private saveCurrentResult(): void {
    this.savedResults.update((map) => new Map(map).set(this.currentSubjectIndex(), this.buildCurrentResult()));
  }

  private buildCurrentResult(): StudentDetailsFormData {
    return {
      preferredFirstName: this.preferredFirstName().trim() || this.firstName(),
      subjectFirstName: this.firstName(),
      subjectLastName: this.lastName(),
      subjectGrade: this.grade(),
      subjectRelationship: this.relationship(),
      subjectTeacherName: this.isClassRoomGroup() ? this.teacherLastName() : null,
      disclaimerAccepted: this.disclaimerAccepted()
    };
  }

  private applyResult(result: StudentDetailsFormState): void {
    this.firstName.set(result.subjectFirstName ?? '');
    this.preferredFirstName.set(result.preferredFirstName ?? '');
    this.lastName.set(result.subjectLastName ?? '');
    this.teacherLastName.set(result.subjectTeacherName ?? '');
    this.grade.set(result.subjectGrade ?? '');
    this.relationship.set(result.subjectRelationship ?? '');
    this.disclaimerAccepted.set(result.disclaimerAccepted ?? false);
  }

  private normalizeGeneratedPlaceholder(value?: string | null): string {
    if (!value) {
      return '';
    }

    return this.subjectStore.isGeneratedFirstNamePlaceholder?.(value) ? '' : value;
  }

  private restoreResult(index: number): void {
    const saved = this.savedResults().get(index);
    const subject = this.getSubjectAt(index);
    const formState = subject ? this.toSubjectFormState(subject, saved) : this.toFormState(saved);
    const disclaimerAccepted = saved?.disclaimerAccepted ?? this.inferDisclaimerAccepted(formState);
    this.applyResult({ ...formState, disclaimerAccepted });
  }

  private inferDisclaimerAccepted(state: StudentDetailsFormState): boolean {
    return Boolean(state.subjectFirstName && state.subjectLastName && state.subjectGrade && state.subjectRelationship);
  }

  private toFormState(saved?: StoredStudentDetails): StudentDetailsFormState {
    if (!saved) {
      return {};
    }

    return {
      subjectFirstName: saved.subjectFirstName,
      preferredFirstName: saved.preferredFirstName,
      subjectLastName: saved.subjectLastName,
      subjectTeacherName: saved.subjectTeacherName,
      subjectGrade: saved.subjectGrade,
      subjectRelationship: saved.subjectRelationship
    };
  }

  private getSubjectAt(index: number): ShoppingCartSubject | undefined {
    return this.subjects().find((_, subjectIndex) => subjectIndex === index);
  }

  private toSubjectFormState(subject: ShoppingCartSubject, saved?: StoredStudentDetails): StudentDetailsFormState {
    return {
      subjectFirstName: this.getRestoredValue(
        saved?.subjectFirstName,
        this.normalizeGeneratedPlaceholder(subject.subjectFirstName)
      ),
      preferredFirstName: this.getRestoredPreferredFirstName(subject, saved),
      subjectLastName: this.getRestoredValue(saved?.subjectLastName, subject.subjectLastName),
      subjectTeacherName: this.getRestoredValue(saved?.subjectTeacherName, subject.subjectTeacherName),
      subjectGrade: this.getRestoredValue(saved?.subjectGrade, subject.subjectGrade),
      subjectRelationship: this.getRestoredValue(saved?.subjectRelationship, subject.subjectRelationship)
    };
  }

  private getRestoredValue(savedValue?: string | null, subjectValue?: string | null): string {
    return savedValue ?? subjectValue ?? '';
  }

  private getRestoredPreferredFirstName(subject: ShoppingCartSubject, saved?: StoredStudentDetails): string {
    if (saved?.preferredFirstName) {
      return saved.preferredFirstName;
    }

    return this.personalizationService.getExistingName({
      id: subject.id,
      allowPersonalizationNamePrefill: subject.allowPersonalizationNamePrefill,
      subjectFirstName: subject.subjectFirstName,
      portraitDashboard: subject.portraitDashboard
    });
  }

  onGoBack(): void {
    this.saveCurrentResult();
    const prevIndex = this.currentSubjectIndex() - 1;
    this.currentSubjectIndex.set(prevIndex);
    this.restoreResult(prevIndex);
  }

  onPreferredFirstNameChange(value: string): void {
    this.preferredFirstName.set(value.slice(0, this.preferredFirstNameMaxLength()));
  }

  onSaveAndContinue(): void {
    if (!this.currentSubject()) {
      this.completedSuccessfully = true;
      this.storageService.remove(SESSION_STORAGE_KEYS.STUDENT_DETAILS_INFO);
      this.dialogRef?.close(this.getModalResult());
      return;
    }

    this.saveCurrentResult();

    if (!this.isLastSubject()) {
      const nextIndex = this.currentSubjectIndex() + 1;
      this.currentSubjectIndex.set(nextIndex);
      this.restoreResult(nextIndex);
      return;
    }

    this.completedSuccessfully = true;
    this.storageService.remove(SESSION_STORAGE_KEYS.STUDENT_DETAILS_INFO);
    const result = this.getModalResult();
    this.completed.emit(result);
    this.dialogRef?.close(result);
  }

  getModalResult(): Partial<ShoppingCartSubject>[] {
    return this.subjects().map((subject, i) => {
      const saved = this.savedResults().get(i);
      // An incomplete entry must not reach checkout: it would overwrite the subject's real names
      // with blanks and mark the details as supplied, and the order would fail on submission.
      if (!saved || !this.isSubjectComplete(subject, i)) return {};
      const { preferredFirstName: _preferredFirstName, disclaimerAccepted: _disclaimerAccepted, ...rest } = saved;
      // Return nameOnText for both explicitly selected personalization and package-included NOGO so
      // syncSubjectEnhancements can stamp the name on the orderable's orderLineItemSelections.
      const hasPersonalization =
        this.personalizationService.hasSelectedPersonalization(subject) || Boolean(subject.hasPackagePersonalization);
      const nameOnText = hasPersonalization ? this.getPersonalizationName(saved) : null;
      return { id: subject.id, nameOnText, ...rest };
    });
  }

  private getPersonalizationName(saved: StoredStudentDetails): string {
    const preferredFirstName = saved.preferredFirstName ?? '';
    const subjectFirstName = saved.subjectFirstName ?? '';

    return preferredFirstName.trim() || subjectFirstName.trim();
  }
}

App.directive('ltStudentDetailsComponent', downgradeComponent({ component: StudentDetailsComponent }));
